// App
import { useSelector } from 'react-redux';

// Constellation
import { Button, ModalTrigger } from '@zillow/constellation-10';
import { Anchor, IconEditOutline, Text, Tooltip, TooltipPopper } from '@zillow/constellation';

// Types
import type { State } from 'app/shared/types/State';

// Misc / Utils
import CONSTANTS from 'app/shared/constants/ConstantsBundle';
import numberUtils from 'app/shared/utils/numberUtils';
import { emitClaimHomeEvent, emitPostListingEvent } from 'app/shared/constants/PriceMyRentalCSEvents';
import * as S from './styles';

const ZestimateCard = ({
  rentZestimate,
  min,
  max,
  zpid,
  priceFromFilter,
}: {
  rentZestimate: number | null;
  min: number;
  max: number;
  zpid: number | null;
  priceFromFilter: number | null;
}) => {
  const user = useSelector((state: State) => state.user);
  const isUserLoggedIn = useSelector((state: State) => state.user.loggedIn);
  const userProperty = useSelector((state: State) => state.address);
  const { addressHasListing } = userProperty;

  const claimHomeURL = `https://www.zillow.com/homedetails/${zpid}_zpid/?view=owner`;
  const tooltip = (
    <Tooltip
      body={
        <Text fontType="bodySmall" as="p">
          A Rent Zestimate<sup>®</sup> is Zillow’s estimate of a property’s monthly rental price. It is computed using
          a formula and is a starting point in determining the monthly rental price for a specific property.
        </Text>
      }
    />
  );

  const handleButtonClick = () => {
    emitPostListingEvent(user);
  };

  const handleClaimYourHomeClick = () => {
    emitClaimHomeEvent(user);
  };

  const createButtonUrl = () => {
    const { street, city, state, zip, unit, zrmListingId } = userProperty;
    const isUserPropertyAvailable = street !== null && city !== null && state !== null && zip !== null;
    let listHomeForRentURL: string = CONSTANTS.ZILLOW_LIST_HOME_FOR_RENT;

    if (isUserLoggedIn && isUserPropertyAvailable && addressHasListing) {
      listHomeForRentURL = listHomeForRentURL.replace('?postingPath=true', '/');
      listHomeForRentURL += zrmListingId + '/listing';
    } else if (isUserPropertyAvailable) {
      const streetKebabCase: string = street.trim().replace(/\s/g, '-');
      const cityKebabCase: string = city.trim().replace(/\s/g, '-');
      listHomeForRentURL += '&address=' + streetKebabCase + '-' + cityKebabCase + '-' + state + '-' + zip;
      listHomeForRentURL += unit != null ? `&unit=` + unit : '';
    }

    return listHomeForRentURL;
  };

  let useRentZestimate: boolean;

  if (rentZestimate && !priceFromFilter) {
    useRentZestimate = true;
  } else if (rentZestimate && priceFromFilter && rentZestimate === priceFromFilter) {
    useRentZestimate = true;
  } else if (priceFromFilter) {
    useRentZestimate = false;
  } else {
    useRentZestimate = false;
  }

  return (
    <S.ZestimateContainer>
      <Text as="h3" fontType="h5">
        {useRentZestimate ? (
          <>
            Your Rent Zestimate<sup>®</sup> <TooltipPopper triggered={tooltip} />
          </>
        ) : (
          <>Custom rental price</>
        )}
      </Text>
      <br />
      {useRentZestimate && (
        <ModalTrigger multiModalTriggerId="change-price-modal">
          <S.PriceText>
            <Text as="h2" fontType="h3">
              {rentZestimate && numberUtils.asCurrency(rentZestimate)}/mo
              <IconEditOutline size="sm" fontColor="brand" marginLeft="xs" style={{ marginTop: '8px' }} />
            </Text>
          </S.PriceText>
        </ModalTrigger>
      )}
      {!useRentZestimate && priceFromFilter && (
        <ModalTrigger multiModalTriggerId="change-price-modal">
          <S.PriceText>
            <Text as="h2" fontType="h3">
              {numberUtils.asCurrency(priceFromFilter)}/mo
              <IconEditOutline size="sm" fontColor="brand" marginLeft="xs" style={{ marginTop: '8px' }} />
            </Text>
          </S.PriceText>
        </ModalTrigger>
      )}
      <br />
      {useRentZestimate && min && max ? (
        <Text as="h4">
          Rent Zestimate<sup>®</sup> range:{' '}
          <strong>
            {numberUtils.asCurrency(min)} - {numberUtils.asCurrency(max)}
          </strong>
        </Text>
      ) : null}
      <Button
        asChild
        size="sm"
        fluid
        tone="brand"
        emphasis="filled"
        onClick={handleButtonClick}
        css={{ marginBottom: '400', marginTop: '600' }}
      >
        <a href={createButtonUrl()} target="_blank" rel="noopener noreferrer">
          {addressHasListing ? 'Access your rental listing' : 'List your home for rent'}
        </a>
      </Button>
      <S.ClaimHomeContainer>
        <S.IconLock />
        <Text fontType="bodySmall" as="p">
          Are you the owner?{' '}
          <Anchor onClick={handleClaimYourHomeClick} href={claimHomeURL} target="_blank">
            Claim your home
          </Anchor>{' '}
          to edit beds, baths, and square footage.
        </Text>
      </S.ClaimHomeContainer>
    </S.ZestimateContainer>
  );
};

export default ZestimateCard;
